import React from 'react';
import PropTypes from 'prop-types';
import styles from './PaymentLineDishes.scss';
import classNames from 'classnames';
import CurrencyUtils from '@wix/wixrest-utils/dist/CurrencyUtils';
import InfoCircle from './InfoCircle';
import { translate } from 'react-i18next';

import { TextButton, Text, Tooltip } from 'wix-style-react';

function Line({ title, price, type = '', light = false, secondary = true }) {
  const baseDataHook = 'payment-line-selected-dish';
  const dataHookPrefix = type ? `${baseDataHook}-${type}` : baseDataHook;
  return (
    <div className={styles.line} data-hook={dataHookPrefix}>
      <Text
        size="small"
        weight="normal"
        light={light}
        secondary={secondary}
        className={styles.stretch}
        data-hook={`${dataHookPrefix}-title`}
      >
        {title}
      </Text>
      <Text size="small" weight="normal" light={light} secondary={secondary} data-hook={`${dataHookPrefix}-price`}>
        {price}
      </Text>
    </div>
  );
}

function PaymentLineDishes({
  onSelectDishesClick,
  orderItems,
  priceComponents,
  displayableDiscounts,
  locale,
  currency,
  t,
}) {
  const isEmpty = orderItems.length === 0;
  const formatPrice = amount => CurrencyUtils.toStr((amount || 0) / 100, currency, locale);

  const emptyState = (
    <div
      className={classNames([styles.selectDishes, styles.emptyState])}
      onClick={onSelectDishesClick}
      data-hook="payment-line-select-dishes-wrapper"
    >
      <TextButton dataHook="payment-line-select-dishes">{t('phoneordering_checkout_split_perdish_select')}</TextButton>
    </div>
  );

  const tooltipContent = (
    <div className={styles.tooltipContent}>
      <Line
        title={t('phoneordering_checkout_split_perdish_additionalcharges_tax_tooltip')}
        price={formatPrice(priceComponents.tax)}
        type="tax"
        light={true}
        secondary={false}
      />
      <Line
        title={t('phoneordering_checkout_split_perdish_additionalcharges_fee_tooltip')}
        price={formatPrice(priceComponents.deliveryFee)}
        type="delivery-fee"
        light={true}
        secondary={false}
      />
      <Line
        title={t('phoneordering_checkout_split_perdish_additionalcharges_discount_tooltip')}
        price={formatPrice(priceComponents.discountSubtotal)}
        type="discounts"
        light={true}
        secondary={false}
      />
    </div>
  );

  const additionalCharges = (
    <React.Fragment>
      <div className={styles.additionalCharges}>{t('phoneordering_checkout_split_perdish_additionalcharges')}</div>
      <div className={styles.info}>
        <Tooltip content={tooltipContent} appendTo="window">
          <InfoCircle />
        </Tooltip>
      </div>
    </React.Fragment>
  );

  const items = (
    <div
      className={classNames([styles.selectDishes, styles.dishList])}
      onClick={onSelectDishesClick}
      data-hook="ppd-select-dishes"
    >
      {orderItems.map((orderItem, index) => {
        return <Line key={index} title={orderItem.title} price={orderItem.price} />;
      })}
      {displayableDiscounts.map((discount, index) => {
        return (
          <Line
            key={`discount-${index}`}
            title={discount.title[locale]}
            price={formatPrice(discount.amount)}
            type="discount"
          />
        );
      })}
      <Line title={additionalCharges} type="additional" />
      <TextButton dataHook="payment-line-edit-selection">{t('phoneordering_checkout_split_perdish_edit')}</TextButton>
    </div>
  );

  return isEmpty ? emptyState : items;
}

PaymentLineDishes.displayName = 'PaymentLineDishes';
PaymentLineDishes.propTypes = {
  onSelectDishesClick: PropTypes.func,
  orderItems: PropTypes.array,
  priceComponents: PropTypes.object,
  displayableDiscounts: PropTypes.array,
  locale: PropTypes.string,
  currency: PropTypes.string,
  t: PropTypes.func,
};

PaymentLineDishes.defaultProps = {
  orderItems: [],
  priceComponents: {},
};

export default translate()(PaymentLineDishes);
